import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

// Real footage, graded to black & white. Each beat pairs one clip with one line.
const BEATS = [
  { src: "/intro/clip1.mp4", text: "We spend so much of our lives searching.", dur: 3000, pos: "50% 50%", zoom: 1.16 },
  { src: "/intro/clip2.mp4", text: "Sometimes forgetting.", dur: 4000, pos: "50% 50%", zoom: 1.16 },
  { src: "/intro/clip3.mp4", text: "The answers were never outside of you.", dur: 3000, pos: "50% 62%", zoom: 1.07 },
];

const CinematicIntro = ({ onEnter }) => {
  const reduce = useReducedMotion();
  const [step, setStep] = useState(0); // 0..2 beats, 3 = ending / CTA
  const [sound, setSound] = useState(false);
  const audioRef = useRef(null);
  const videoRefs = useRef([]);

  // Choreography timeline synced to clip lengths
  useEffect(() => {
    if (reduce) {
      setStep(3);
      return;
    }
    const t0 = BEATS[0].dur;
    const t1 = t0 + BEATS[1].dur;
    const t2 = t1 + BEATS[2].dur;
    const timers = [
      setTimeout(() => setStep(1), t0),
      setTimeout(() => setStep(2), t1),
      setTimeout(() => setStep(3), t2),
    ];
    return () => timers.forEach(clearTimeout);
  }, [reduce]);

  // Play the active clip from its first frame
  useEffect(() => {
    if (step > 2) return;
    const v = videoRefs.current[step];
    if (v) {
      try {
        v.currentTime = 0;
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      } catch (e) {}
    }
  }, [step]);

  // Pause the smooth-scroll engine while the intro plays
  useEffect(() => {
    window.__lenis?.stop?.();
    window.__lenis?.scrollTo?.(0, { immediate: true });
    return () => window.__lenis?.start?.();
  }, []);

  // Prepare the ambient bed and try to start it with the film. Browsers block
  // autoplay with sound until a gesture — the "sound on" invitation + first-tap
  // fallback ensure the music accompanies the intro as soon as it's allowed.
  useEffect(() => {
    const audio = new Audio("/intro/ambient.mp3?v=soundbath");
    audio.loop = true;
    audio.volume = 0.6;
    audio.preload = "auto";
    audioRef.current = audio;

    let cancelled = false;
    audio.play().then(() => {
      if (!cancelled) setSound(true);
    }).catch(() => {
      /* autoplay blocked — visitor can tap the invitation */
    });

    const onFirstGesture = () => {
      if (cancelled) return;
      audio.play().then(() => setSound(true)).catch(() => {});
    };
    window.addEventListener("pointerdown", onFirstGesture, { once: true });

    return () => {
      cancelled = true;
      window.removeEventListener("pointerdown", onFirstGesture);
      try { audio.pause(); audio.currentTime = 0; } catch (e) {}
    };
  }, []);

  const toggleSound = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = !sound;
    setSound(next);
    if (next) audio.play().catch(() => {});
    else audio.pause();
  };

  const handleEnter = () => {
    try {
      audioRef.current?.pause();
    } catch (e) {}
    onEnter();
  };

  return (
    <motion.div
      className="fixed inset-0 z-[300] overflow-hidden bg-black"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.2, ease: EASE }}
      data-testid="cinematic-intro"
    >
      {/* Video sequence with slow Ken-Burns drift */}
      {BEATS.map((b, i) => (
        <motion.div
          key={i}
          className="absolute inset-0"
          animate={{ opacity: step === i ? 1 : 0 }}
          transition={{ duration: 1.3, ease: EASE }}
        >
          <motion.video
            ref={(el) => (videoRefs.current[i] = el)}
            src={b.src}
            poster="/intro/poster.jpg"
            muted
            playsInline
            preload="auto"
            className="h-full w-full object-cover"
            style={{ objectPosition: b.pos }}
            initial={{ scale: 1.04 }}
            animate={{ scale: step === i ? b.zoom : 1.04 }}
            transition={{ duration: b.dur / 1000 + 2, ease: "linear" }}
          />
        </motion.div>
      ))}

      {/* Cinematic overlays: darkening, grain, vignette */}
      <div className="pointer-events-none absolute inset-0 bg-black/30" />
      <div className="gaia-grain pointer-events-none absolute inset-0 opacity-80" />
      <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_240px_70px_rgba(0,0,0,0.8)]" />
      {/* Subtle letterbox bars for a filmic frame */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[7vh] bg-black md:h-[9vh]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[7vh] bg-black md:h-[9vh]" />

      {/* Sentences */}
      <div className="absolute inset-0 flex items-center justify-center px-8">
        <AnimatePresence mode="wait">
          {step < 3 && (
            <motion.p
              key={step}
              className="max-w-3xl text-center font-serif text-3xl font-light leading-snug tracking-tight text-white/90 drop-shadow-[0_2px_24px_rgba(0,0,0,0.7)] md:text-5xl"
              initial={{ opacity: 0, y: 18, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -14, filter: "blur(8px)" }}
              transition={{ duration: 1.3, ease: EASE }}
              data-testid="intro-line"
            >
              {BEATS[step].text}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Ending: fade to black, reveal the name + invitation */}
      <AnimatePresence>
        {step === 3 && (
          <motion.div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black px-8 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.6, ease: EASE }}
          >
            <motion.h1
              className="font-serif text-5xl font-light tracking-[0.04em] text-white md:text-7xl"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.6, delay: reduce ? 0.2 : 0.9, ease: EASE }}
              data-testid="intro-brand"
            >
              GAIA SPACE
            </motion.h1>
            <motion.p
              className="mt-6 font-sans text-[11px] uppercase tracking-[0.34em] text-white/55 md:text-xs"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.4, delay: reduce ? 0.4 : 1.8, ease: EASE }}
            >
              A space to remember who you are.
            </motion.p>
            <motion.button
              onClick={handleEnter}
              className="group relative mt-12 overflow-hidden border border-white/40 px-10 py-4 font-sans text-[11px] uppercase tracking-[0.24em] text-white"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: reduce ? 0.6 : 2.6, ease: EASE }}
              data-testid="enter-gaia-btn"
            >
              <span
                aria-hidden
                className="absolute inset-0 origin-bottom scale-y-0 bg-white transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-y-100"
              />
              <span className="relative z-10 block transition-colors duration-500 group-hover:text-black">
                Enter Gaia Space
              </span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ambient sound — muted by default; an understated invitation to turn it on */}
      <div className="absolute right-6 top-6 z-20 flex items-center gap-3 md:right-10 md:top-10">
        <AnimatePresence>
          {!sound && step < 3 && (
            <motion.span
              key="sound-hint"
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.9, ease: EASE, delay: 1.4 }}
              className="hidden font-sans text-[10px] uppercase tracking-[0.28em] text-white/55 sm:inline"
            >
              Sound on
            </motion.span>
          )}
        </AnimatePresence>
        <button
          onClick={toggleSound}
          aria-label={sound ? "Mute ambient sound" : "Enable ambient sound"}
          className="relative flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-white/70 transition-colors duration-500 hover:text-white"
          data-testid="intro-sound-toggle"
        >
          {!sound && (
            <span
              aria-hidden
              className="absolute inset-0 animate-ping rounded-full border border-white/20"
              style={{ animationDuration: "2.8s" }}
            />
          )}
          {sound ? <Volume2 size={15} strokeWidth={1.4} /> : <VolumeX size={15} strokeWidth={1.4} />}
        </button>
      </div>

      {/* Understated skip */}
      {step < 3 && (
        <button
          onClick={() => setStep(3)}
          className="absolute bottom-6 right-6 z-20 font-sans text-[10px] uppercase tracking-[0.3em] text-white/40 transition-colors duration-500 hover:text-white/80 md:bottom-10 md:right-10"
          data-testid="intro-skip-btn"
        >
          Skip
        </button>
      )}
    </motion.div>
  );
};

export default CinematicIntro;
